import {formatLocaleDate} from '@/dayjs.ts'
import {useTranslation} from '@/translations/i18n.tsx'
import {
    UserBaseInfoFormValidationSchema,
    UserExtended,
    UserLocationInfoFormValidationSchema
} from '@/features/users/types.ts'
import {UserPersonalDataForm} from '@/features/users/components/user-personal-data-form/UserPersonalDataForm'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {UserResidenceForm} from '@/features/users/components/user-residence-form/UserResidenceForm'
import {UserDomicileForm} from '@/features/users/components/user-domicile-form/UserDomicileForm'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {maritalStatusesToLabel, ObjectKeys} from '@/utilities/helpers'
import {FemaleIcon, MaleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {DetailsField, DetailsFieldsGrid, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {zodResolver} from '@hookform/resolvers/zod'
import {getUserDirtyFieldsFromFormValues, remapUserToDefaultValues, remapUserToPayload} from '@/features/users/utils'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {Text} from '@/components/ui/text/Text'
import {z} from 'zod'

export const UserPersonalDataFormValidationSchema = UserBaseInfoFormValidationSchema.and(
    UserLocationInfoFormValidationSchema
)
type UserPersonalDataFormValidationSchema = z.infer<typeof UserPersonalDataFormValidationSchema>

export const UserDetailsInformationPersonalData = ({user}: {user: UserExtended}) => {
    const form = useForm<UserPersonalDataFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserPersonalDataFormValidationSchema),
        defaultValues: remapUserToDefaultValues(user)
    })

    return (
        <FormProvider {...form}>
            <UserDetailsInformationPersonalDataContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationPersonalDataContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('personal-data')
    const {palette} = useTheme()
    const form = useFormContext<UserPersonalDataFormValidationSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserPersonalDataFormValidationSchema
    })

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )
    return (
        <DetailsFieldsSection
            title={t('commons:personal_data')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="personal-data"
                    isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                    onCancel={form.reset}
                    onConfirm={handleSubmit}
                />
            }
        >
            {isEditModeEnabled ? (
                <Flexbox direction="column" gap={8} fullWidth>
                    <UserPersonalDataForm />
                    <UserResidenceForm
                        defaultCoordinates={{
                            latitude: user.residence.city.latitude,
                            longitude: user.residence.city.longitude
                        }}
                    />
                    <UserDomicileForm
                        defaultCoordinates={{
                            latitude: user.domicile.city.latitude,
                            longitude: user.domicile.city.longitude
                        }}
                    />
                </Flexbox>
            ) : (
                <DetailsFieldsGrid>
                    <DetailsField>
                        <Flexbox gap={1}>
                            <Text size="textMd" weight="500">
                                {user.fullName}
                            </Text>
                            {user.gender == 'male' && <MaleIcon size={18} color={palette.primary[600]} />}
                            {user.gender == 'female' && <FemaleIcon size={18} color={palette.purple[600]} />}
                        </Flexbox>
                        <div>
                            <Text palette="neutral" shade="500" size="textSm">
                                {user.birthCity
                                    ? t('commons:birth_date_city', {
                                          date: formatLocaleDate(user.birthDate, 'L'),
                                          city: `${user.birthCity.name} (${user.birthCity.provinceAcronym})`
                                      })
                                    : t('commons:birth_date_country', {
                                          date: formatLocaleDate(user.birthDate, 'L'),
                                          country: user.birthCountry.name
                                      })}
                            </Text>
                            {user.maritalStatus && (
                                <Text size="textSm">{t(maritalStatusesToLabel[user.maritalStatus])}</Text>
                            )}
                        </div>
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:fiscal_code')}
                        </Text>
                        <Text size="textMd" weight={500}>
                            {user.taxCode}
                        </Text>
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:residence')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {`${user.residence.address} ${user.residence.streetNumber}, ${user.residence.city.name} (${user.residence.city.provinceAcronym}) ${user.residence.cap}`}
                        </Text>
                        <Text palette="neutral" shade="500" size="textSm">
                            {user?.residenceAslDistrict?.aslName ?? t('commons:out_region')}
                        </Text>
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:domicile')}
                        </Text>
                        {user.domicile.id == user.residence.id ? (
                            <Text palette="neutral" shade="500" size="textMd">
                                {t('commons:same_as_residence')}
                            </Text>
                        ) : (
                            <>
                                <Text size="textMd" weight="500">
                                    {`${user.domicile.address} ${user.domicile.streetNumber}, ${user.domicile.city.name} (${user.domicile.city.provinceAcronym}) ${user.domicile.cap}`}
                                </Text>
                                <Text palette="neutral" shade="500" size="textMd">
                                    {user?.domicileAslDistrict?.aslName ?? t('commons:out_region')}
                                </Text>
                            </>
                        )}
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:email')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {user.email}
                        </Text>
                        {user.role && (
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('users:used_to_access_star')}
                            </Text>
                        )}
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('users:pec')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {user.pec ?? '-'}
                        </Text>
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:mobile_phone')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {user.phoneNumber}
                        </Text>
                    </DetailsField>
                </DetailsFieldsGrid>
            )}
        </DetailsFieldsSection>
    )
}
